import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SVG } from "../../../constants/assetLists";
import {
  BranchData,
  ClientDataType,
  SubBranchData,
  getLocationFilterParams,
  useClientsContext,
} from "../../../context/clients/ClientsContextProvider";
import { useNavigate } from "react-router";
import { isAdmin, isSuperAdmin, isSuperAdminOnly } from "../../../utils";

type Props = {
  showCopyIndicator?: boolean;
  clearDaySummaryFormFilter?: () => void;
};

function ClientsSideFilter({
  showCopyIndicator = false,
  clearDaySummaryFormFilter,
}: Props) {
  const { i18n } = useTranslation();
  const {
    allClients,
    selectedLocation,
    setSelectedLocation,
    recentlyCopiedLocationIds,
    setRecentlyCopiedLocationIds,
  } = useClientsContext();

  const sidebarRef = useRef<HTMLDivElement>(null);

  const [filterSidebarVariant, setFilterSidebarVariant] = useState<
    "full" | "compact" | "hidden"
  >("full");
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [hasRestoredExpansion, setHasRestoredExpansion] = useState(false);
  const navigate = useNavigate();

  const isHebrew = useMemo(() => i18n.language === "heb", [i18n.language]);
  const isFull = useMemo(
    () => filterSidebarVariant === "full",
    [filterSidebarVariant],
  );
  const isCompact = useMemo(
    () => filterSidebarVariant === "compact",
    [filterSidebarVariant],
  );

  const handleNavigateToOnBoarding = () => {
    // A super admin edits a specific organization's tree — target it by client id
    if (isSuperAdminOnly() || isAdmin()) {
      const clientId =
        getLocationFilterParams(selectedLocation).clientId ??
        allClients?.[0]?.id;
      navigate(clientId ? `/on-boarding?clientId=${clientId}` : "/on-boarding");
      return;
    }
    navigate("/on-boarding");
  };

  // Auto-expand parent nodes to show selected location
  useEffect(() => {
    if (!selectedLocation || hasRestoredExpansion) return;

    const expandParents = () => {
      const nodesToExpand = new Set<string>();

      // Recursively find the path of ancestor IDs leading to targetId.
      // Handles 3 levels: Client → Branch (via .branches) → SubBranch (via .subBranches).
      const findPath = (
        nodes: (ClientDataType | BranchData | SubBranchData)[],
        targetId: string,
        path: string[] = [],
      ): string[] | null => {
        for (const node of nodes) {
          if (node.id === targetId) return path;

          const children: (BranchData | SubBranchData)[] = node.isClient
            ? (node as ClientDataType).branches || []
            : node.isBranch
              ? (node as BranchData).subBranches || []
              : [];

          if (children.length > 0) {
            const result = findPath(children, targetId, [...path, node.id]);
            if (result) return result;
          }
        }
        return null;
      };

      // Find path to selected node
      const path = findPath(allClients, selectedLocation.id);

      if (path) {
        path.forEach((id) => nodesToExpand.add(id));
        setExpandedSet(nodesToExpand);
        setHasRestoredExpansion(true);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(expandParents, 100);
  }, [selectedLocation, allClients, hasRestoredExpansion]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !sidebarRef.current) return;

      const newWidth = isHebrew ? window.innerWidth - e.clientX : e.clientX;

      if (newWidth >= 209) {
        setFilterSidebarVariant("full");
        setIsDragging(false);
        return;
      }

      const width = `${Math.max(newWidth, 86)}px`;
      sidebarRef.current.style.width = width;
    };

    const stopDragging = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", stopDragging);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
    };
  }, [isDragging, isHebrew, setFilterSidebarVariant]);

  React.useEffect(() => {
    if (!sidebarRef.current) return;

    sidebarRef.current.style.width = isFull
      ? "302px"
      : isCompact
        ? "86px"
        : "24px";
  }, [filterSidebarVariant, isCompact, isFull]);

  const handleSidebarWidthChange = () => {
    setFilterSidebarVariant((prev) =>
      prev === "full" ? "compact" : prev === "compact" ? "hidden" : "full",
    );
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  // toggle expand/collapse by id
  const toggleExpand = (id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // When a node is clicked → select it
  const handleSelect = (node: ClientDataType | BranchData | SubBranchData) => {
    setSelectedLocation(node);

    // Remove from recently copied list when visited (only on checklist page)
    if (showCopyIndicator && recentlyCopiedLocationIds.includes(node.id)) {
      setRecentlyCopiedLocationIds((prev) =>
        prev.filter((id) => id !== node.id),
      );
    }
  };

  // Recursive item — handles 3 levels: Client → Branch → SubBranch
  const TreeItem: React.FC<{
    node: ClientDataType | BranchData | SubBranchData;
    depth: number;
  }> = ({ node, depth }) => {
    // Resolve children based on node type:
    //   ClientDataType  → .branches (BranchData[])
    //   BranchData      → .subBranches (SubBranchData[])
    //   SubBranchData   → leaf, no children
    const children: (BranchData | SubBranchData)[] = node.isClient
      ? (node as ClientDataType).branches || []
      : node.isBranch
        ? (node as BranchData).subBranches || []
        : [];

    const hasChildren = children.length > 0;
    const expanded = expandedSet.has(node.id);
    const isSelected = selectedLocation?.id === node.id;
    const isRecentlyCopied =
      showCopyIndicator && recentlyCopiedLocationIds.includes(node.id);

    const paddingInline = isFull ? Math.min(12 + depth * 16, 56) : 0;
    const rounded = isHebrew
      ? "rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px]"
      : "rounded-tr-[30px] rounded-br-[30px] rounded-tl-[6px] rounded-bl-[6px]";

    const caretIcon = expanded ? SVG.dropdownIcon : SVG.dropUpIcon;

    const onClick = () => {
      if (hasChildren) toggleExpand(node.id);
      handleSelect(node);
      clearDaySummaryFormFilter?.();
    };

    return (
      <div>
        <button
          key={node.id}
          className={`w-full flex items-center py-2 transition-colors duration-200 hover:bg-hoverBg
            ${isFull ? "justify-between" : "justify-center"} ${rounded}
            ${isSelected ? "bg-softAqua" : ""}
          `}
          style={{
            paddingInlineStart: `${paddingInline}px`,
            paddingInlineEnd: "8px",
          }}
          onClick={onClick}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              {hasChildren && (
                <div className="w-[15px] h-[10px]">
                  <img
                    src={caretIcon}
                    alt="caret"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div className="w-[20px] h-[20px]">
                <img
                  src={node.icon}
                  alt="icon"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            {isFull && <span className="text-black">{node?.name}</span>}
            {isRecentlyCopied && (
              <div className="w-[8px] h-[8px] rounded-full bg-blue-500 flex-shrink-0" />
            )}
          </div>
          {isFull && (
            <span className="text-black">
              {hasChildren ? children.length : ""}
            </span>
          )}
        </button>

        {hasChildren && expanded && (
          <div className="mt-1 flex flex-col gap-1">
            {children.map((child) => (
              <TreeItem key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={sidebarRef}
      className={`flex-none relative transition-all duration-150 h-full bg-[#f4f4f4] ${
        i18n.language == "heb"
          ? `border-l-2 ${isDragging ? "border-l-[#007AFF]" : "border-l-black"}`
          : `border-r-2 ${isDragging ? "border-r-[#007AFF]" : "border-r-black"}`
      }`}
    >
      <div
        className={`${
          filterSidebarVariant !== "hidden" && "px-3"
        } py-3 border-b border-b-lightDarkGray`}
      >
        <div
          className={`flex ${
            isFull ? "justify-between" : "justify-center"
          }  items-center`}
        >
          {isFull && (
            <span className="text-[20px] font-light leading-6 text-black">
              מקומות
            </span>
          )}

          <button
            className="w-[12px] h-[12px] hover:opacity-40"
            onClick={handleSidebarWidthChange}
          >
            <img
              src={
                filterSidebarVariant == "hidden"
                  ? SVG.lessThanIcon
                  : SVG.greaterThanIcon
              }
              alt="add"
              className="w-full h-full object-contain"
            />
          </button>
        </div>
      </div>

      <div className="w-full h-full">
        {(isFull || isCompact) && (
          <div className="w-full h-full flex flex-col gap-2 py-3">
            <div
              className={`flex-1 min-h-0 flex flex-col px-3 gap-2 overflow-y-auto`}
            >
              {allClients?.map((root) => (
                <TreeItem key={root.id} node={root} depth={0} />
              ))}
            </div>

            {(isSuperAdmin() || isAdmin()) && (
              <div className="m-4 p-2 pb-4 border-t border-t-white flex items-center gap-2">
                <div
                  className="w-[32px] h-[32px] cursor-pointer"
                  onClick={handleNavigateToOnBoarding}
                >
                  <img
                    src={SVG.editSettingIcon}
                    alt="tree"
                    className="w-full h-full object-contain"
                  />
                </div>

                {isFull && (
                  <button
                    onClick={handleNavigateToOnBoarding}
                    className="text-black80"
                  >
                    עריכת עץ ארגון
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {isCompact && (
        <div
          className="absolute top-0 bottom-0 w-[6px] cursor-col-resize z-1"
          style={{
            [i18n.language === "heb" ? "left" : "right"]: 0,
          }}
          onMouseDown={handleMouseDown}
        />
      )}
    </div>
  );
}

export default ClientsSideFilter;
